import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";
import { TrajectoryPointDtoVehicleType } from "@/api/generated/models/trajectoryPointDtoVehicleType";

const customConfig = defineConfig({
  globalCss: {
    body: {
      margin: 0,
      padding: 0,
      backgroundColor: "background",
      fontFamily: "body",
    },
  },
  theme: {
    tokens: {
      colors: {
        // Primary brand
        mobilysisBlue: { value: "#002677" },
        mobilysisGreen: { value: "#89C765" },
        black: { value: "#000000" },
        white: { value: "#FFFFFF" },

        // Secondary brand
        maroon: { value: "#B50000" },
        red: { value: "#FF0055" },
        magenta: { value: "#FF41DF" },
        brown: { value: "#994500" },
        orange: { value: "#FF9500" },
        mustard: { value: "#F2CB00" },
        green: { value: "#148800" },
        lime: { value: "#00CC41" },
        turquoise: { value: "#00CFC1" },
        indigo: { value: "#60008D" },
        blue: { value: "#2F00FF" },
        cyan: { value: "#00BFFF" },

        // Grey tones
        grey: {
          50: { value: "#F7FAFC" },
          100: { value: "#EDF2F7" },
          200: { value: "#E2E8F0" },
          300: { value: "#CBD5E0" },
          400: { value: "#A0AEC0" },
          500: { value: "#718096" },
          600: { value: "#4A5568" },
          700: { value: "#2D3748" },
          800: { value: "#1A202C" },
          900: { value: "#171923" },
        },
      },
      fonts: {
        body: { value: "'Plus Jakarta Sans', sans-serif" },
        heading: { value: "'Plus Jakarta Sans', sans-serif" },
        mono: { value: "'Fira Code', monospace" },
      },
      fontSizes: {
        xs: { value: "0.75rem" },
        sm: { value: "0.875rem" },
        md: { value: "1rem" },
        lg: { value: "1.125rem" },
        xl: { value: "1.25rem" },
        "2xl": { value: "1.5rem" },
        "3xl": { value: "1.875rem" },
        "4xl": { value: "2.25rem" },
      },
      fontWeights: {
        normal: { value: "400" },
        medium: { value: "500" },
        semibold: { value: "600" },
        bold: { value: "700" },
        extrabold: { value: "800" },
      },
      borderWidths: {
        tab: { value: "4px" },
        button: { value: "2px" },
      },
      radii: {
        pill: { value: "44px" },
        card: { value: "16px" },
        button: { value: "8px" },
      },
      sizes: {
        legendCard: { value: "18.25rem" },
        legendLabel: { value: "6.25rem" },
      },
      spacing: {
        0: { value: "0" },
        1: { value: "0.25rem" },
        2: { value: "0.5rem" },
        3: { value: "0.75rem" },
        4: { value: "1rem" },
        5: { value: "1.25rem" },
        6: { value: "1.5rem" },
        8: { value: "2rem" },
        10: { value: "2.5rem" },
        12: { value: "3rem" },
        16: { value: "4rem" },
      },
    },
    semanticTokens: {
      shadows: {
        elevated: {
          value: "0 6px 16px rgba(113, 128, 150, 0.4)",
        },
      },
      colors: {
        primary: { value: { base: "{colors.mobilysisBlue}" } },
        secondary: { value: { base: "{colors.mobilysisGreen}" } },
        accent: { value: { base: "{colors.cyan}" } },
        background: { value: { base: "{colors.grey.50}" } },
        text: { value: { base: "{colors.grey.900}" } },
        textMuted: { value: { base: "{colors.grey.600}" } },
        border: { value: { base: "{colors.grey.200}" } },
        error: { value: { base: "{colors.red}" } },
        success: { value: { base: "{colors.green}" } },
        warning: { value: { base: "{colors.orange}" } },
        info: { value: { base: "{colors.cyan}" } },
        cardBg: { value: { base: "{colors.white}" } },
      },
    },
  },
});

export const system = createSystem(defaultConfig, customConfig);

export const vehicleTypeColors: Record<TrajectoryPointDtoVehicleType, string> =
  {
    car: system.token("colors.blue"),
    bus: system.token("colors.green"),
    motorcycle: system.token("colors.red"),
    bike: system.token("colors.mustard"),
    truck: system.token("colors.brown"),
    pedestrian: system.token("colors.magenta"),
  };

export const movementColors: readonly string[] = [
  system.token("colors.blue"),
  system.token("colors.red"),
  system.token("colors.green"),
  system.token("colors.orange"),
  system.token("colors.indigo"),
  system.token("colors.cyan"),
  system.token("colors.magenta"),
  system.token("colors.turquoise"),
  system.token("colors.brown"),
  system.token("colors.mustard"),
  system.token("colors.maroon"),
] as const;

export const junctionColors = {
  gate: system.token("colors.grey.700"),
  gateLabel: system.token("colors.white"),
  marker: system.token("colors.grey.700"),
  markerHighlight: system.token("colors.mobilysisGreen"),
} as const;

export const gradientColors = {
  speed: {
    min: system.token("colors.green"),
    mid: system.token("colors.mustard"),
    max: system.token("colors.red"),
  },
  acceleration: {
    min: system.token("colors.indigo"),
    mid: system.token("colors.grey.300"),
    max: system.token("colors.orange"),
  },
  density: {
    min: system.token("colors.cyan"),
    mid: system.token("colors.mobilysisBlue"),
    max: system.token("colors.indigo"),
  },
};
